import { logout as logoutRequest } from '@np-forge/api-client/auth'
import type { UserWithToSResponse } from '@np-forge/api-client/types'
import { getMe } from '@np-forge/api-client/users'
import * as Sentry from '@sentry/react'
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

import { buildAuthorizeUrl } from '@/api/client'
import { identifyUser, resetUser } from '@/lib/analytics'
import { errorMessage, errorStatus } from '@/lib/errors'

interface AuthContextValue {
  user: UserWithToSResponse | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  // Replace the cached user from a known-fresh payload (e.g. a settings PATCH
  // response) without a /me round-trip or a loading flash.
  applyUser: (user: UserWithToSResponse) => void
  login: (provider: string) => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserWithToSResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = async () => {
    // Single source of truth for session restore on mount and later manual refreshes
    setLoading(true)
    setError(null)
    try {
      const me = await getMe()
      setUser(me)
    } catch (e: unknown) {
      // No active session is an expected state on login screen or after logout
      setUser(null)
      const status = errorStatus(e)
      if (status && status !== 401 && status !== 404) {
        setError(errorMessage(e, 'Failed to load user'))
      }
    } finally {
      setLoading(false)
    }
  }

  const applyUser = (next: UserWithToSResponse) => {
    setUser(next)
  }

  useEffect(() => {
    // Always restore session on mount (cookie-based). Route guards should wait for `loading`.
    // We intentionally call the shared `refresh()` to keep logic centralized.
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Single source of truth for Amplitude + Sentry identity: identify on first non-null
  // user, reset on transitions back to null (logout OR silent 401 session expiry).
  // The ref avoids firing resetUser on the initial mount when user was already null.
  // We pass only the synthetic backend id — never email or name.
  const prevUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    const currentId = user?.user.id ?? null
    const prevId = prevUserIdRef.current

    if (currentId && currentId !== prevId) {
      identifyUser(currentId)
      Sentry.setUser({ id: currentId })
    } else if (!currentId && prevId) {
      resetUser()
      Sentry.setUser(null)
    }

    prevUserIdRef.current = currentId
  }, [user?.user.id])

  const login = (provider: string) => {
    // Start OAuth flow; the server will set the session cookie on return.
    window.location.href = buildAuthorizeUrl(provider)
  }

  const logout = async () => {
    try {
      await logoutRequest()
    } finally {
      // Amplitude identity is severed by the identify-effect when user transitions
      // to null — uniform handling for explicit logout AND silent 401 session expiry.
      setUser(null)
      setError(null)
    }
  }

  const value: AuthContextValue = { user, loading, error, refresh, applyUser, login, logout }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
